// Posts service - complete post data with entities and category counts
// All endpoints use POST for security (no IDs in URLs)

import { apiClient, ApiClientError } from "@/lib/api/client";

export interface DiscoverSession {
  userId: string;
  isCurrent: () => boolean;
}

// Entity breakdown types
export interface ExtractedEntities {
  places?: Array<{ entity_id: string; name: string; confidence: number }>;
  people?: Array<{ entity_id: string; name: string; confidence: number }>;
  brands?: Array<{ entity_id: string; name: string; confidence: number }>;
  products?: Array<{ entity_id: string; name: string; confidence: number }>;
  events?: Array<{ entity_id: string; name: string; confidence: number }>;
  activities?: Array<{ entity_id: string; name: string; confidence: number }>;
}

export interface PostsCounts {
  intent?: Record<string, number>;
  routing?: Record<string, number>;
  category?: Record<string, number>;
  source?: Record<string, number>; // TODO: Backend needs to populate this field in /posts/filters response
}

export interface PostsFiltersRequest {
  date_from?: string;
  date_to?: string;
}

// PostsFiltersResponse is an alias for PostsCounts
export type PostsFiltersResponse = PostsCounts;

// Media item from API
export interface PostMedia {
  type?: string;
  url?: string;
  cdn_url?: string;
  original_url?: string;
  original_instagram_url?: string;
  s3_key?: string;
  archived_at?: string;
  file_size?: string;
  width?: number | string;
  height?: number | string;
  width_px?: number | string;
  height_px?: number | string;
  dimensions?: {
    width?: number | string;
    height?: number | string;
  };
  rotation?: number;
  // OCR/vision data embedded on a media item
  ocr_text?: string;
  vision_entities?: string;
  // Transcript for videos
  transcript?: string;
  transcript_language?: string;
  // Thumbnail reference for videos
  is_thumbnail?: boolean;
  source_media_index?: number;
}

export interface ImageSignal {
  index?: number;
  s3_key?: string;
  ocr_text?: string;
  vision_entities?: string;
  success?: boolean;
  error?: string;
}

/**
 * A page of Explore's matches: ids in rank order. The posts it embeds are a slim public
 * projection, not a Post; stashes are read by id like every other list's.
 */
export interface PublicSearchPage {
  ids: string[];
  next_cursor: string | null;
}

// Post type - matches actual API response from _format_post
export interface Post {
  // Identity - stasht_id is the canonical ID
  id: string;
  stasht_id: string;

  // Timestamps
  created_at: string;

  // Content fields
  title: string;
  caption: string;
  text: string;
  full_text: string;
  vision_description: string;

  // Media
  attachment_type: string;
  media_url: string;
  media?: PostMedia[];
  image_content?: ImageSignal[];

  // Location
  locations: string[];
  location_city: string;

  // Classification
  activity_type: string;
  sentiment: string;
  overall_message_intent: string;
  auto_categories?: Array<{
    routing_class?: string;
    category?: string;
  }>;

  // Processing status
  is_merged_child: boolean;
  is_raw_only: boolean;
  needs_enrichment: boolean;
  enrichment_status?: string;
  last_enriched_at?: string;

  // URLs
  original_url?: string;
  canonical_url?: string;

  // Source info
  platform?: string;
  username?: string;
  source?: { platform?: string; username?: string; type?: string; url?: string };

  // Entity data
  extracted_entities?: ExtractedEntities;
  entity_ids?: string[]; // Derived from extracted_entities or stored separately

  // Tags
  tags?: string[];

  // Other data
  attachment_data?: Record<string, unknown>;
  unfurl_status?: "success" | "age_restricted" | "private" | "empty";
  pipeline?: PipelineStatus;

  // Ingest metadata - how the content entered the system
  ingest?: {
    method?: "dm" | "url";  // "dm" = Instagram DM, "url" = iOS share with canonical URL
    source?: string;        // "instagram_dm", "ios_share", etc.
  };

  // Content metadata from unfurl (creator info, original post date)
  content?: {
    creator_username?: string;
    original_posted_at?: string;
    caption?: string;
    content_type?: string;
    media?: PostMedia[];
    media_urls?: PostMedia[];
    url?: string;
    title?: string;
    description?: string;
    possibly_sensitive?: boolean;
  };

  // Visibility
  visibility?: "private" | "public";
  publication?: "private" | "public" | "unknown";
  source_type?: string;

  // User-specific
  is_saved?: boolean; // @deprecated - use user_state
  user_state?: 'active' | 'removed' | 'none';
  user_title?: string; // Per-user title override from stasht_user_content
  can_edit_sensitive?: boolean; // Server-authorized original poster/admin capability

  // Map posts only: entity ids on this post that intersect the requested bbox/category.
  matching_entity_ids?: string[];
}

export interface StashIdListRequest {
  limit?: number;
  cursor?: string | null;
  routing_class?: string;
  category?: string;
  source?: string;
  overall_message_intent?: string;
  date_from?: string;
  date_to?: string;
  sort?: "asc" | "desc";
}

export interface StashIdListResponse {
  stash_ids: string[];
  next_cursor: string | null;
}

export interface MapPostsRequest {
  scope?: "public";
  bbox: {
    min_lat: number;
    min_lng: number;
    max_lat: number;
    max_lng: number;
  };
  visible_entity_ids?: string[];
  entity_category_key?: string;
  sort?: "recent";
  limit?: number;
  cursor?: string | null;
}

export interface MapPostsResponse {
  scope: "public";
  bbox: MapPostsRequest["bbox"];
  posts: Post[];
  count: number;
  limit: number;
  next_cursor?: string | null;
  entity_category_key_filter?: string;
}

// Get single post request
export interface PostGetRequest {
  id: string;
  include_entities?: boolean; // Default true
}

// Batch request for multiple posts
export interface PostsBatchRequest {
  ids: string[]; // Backend expects 'ids', not 'message_mids'
  include_entities?: boolean; // Default true
}

export interface PostsBatchResponse {
  posts: Record<string, Post>; // Keyed by stasht_id
  not_found: string[]; // IDs that weren't found
  user_states?: Record<string, "active" | "processing" | "failed" | "removed" | "none">;
}

export interface DiscoverReason {
  seed_id: string;
  interest: "recent" | "durable";
}

export interface DiscoverPost extends Partial<Post> {
  stasht_id: string;
  sensitive?: boolean;
  user_saved_at?: string;
  media?: Array<PostMedia & { original_media_url?: string; is_cover?: boolean }>;
}

export interface DiscoverPage extends Omit<PostsBatchResponse, "posts"> {
  posts: Record<string, DiscoverPost>;
  ids: string[];
  reasons?: Record<string, DiscoverReason>;
  has_more: boolean;
  reason?: "no_seeds" | null;
}

// Pipeline status types (from stasht_posts table - used for progressive loading)
export interface PipelineStageStatus {
  status: "pending" | "unfurling" | "downloading" | "enriching" | "succeeded" | "failed" | "not_applicable";
  updated_at?: string;
  failure_reason?: string;
}

export interface PipelineStatus {
  status: string;
  updated_at?: string;
  unfurl?: PipelineStageStatus;
  media?: PipelineStageStatus;
  enrich?: PipelineStageStatus;
  discover?: PipelineStageStatus;
}

export interface PipelinePostContent {
  title?: string;
  description?: string;
  media?: PostMedia[];
  creator_username?: string;
}

export interface PipelinePostSource {
  platform?: string;
  url?: string;
}

export interface PipelinePostItem {
  pipeline?: PipelineStatus;
  content?: PipelinePostContent;
  source?: PipelinePostSource;
}

export interface PipelineStatusResponse {
  items: Record<string, PipelinePostItem>;
  requested: number;
  found: number;
}

class PostsService {
  private static instance: PostsService;

  static getInstance(): PostsService {
    if (!PostsService.instance) {
      PostsService.instance = new PostsService();
    }
    return PostsService.instance;
  }

  private async apiCall<T>(endpoint: string, body?: unknown, skipAuth = false): Promise<T> {
    try {
      return await apiClient.post<T>(endpoint, body ?? {}, skipAuth);
    } catch (error) {
      console.error(`API call failed: ${endpoint}`, error);
      throw error;
    }
  }

  async listStashIds(request: StashIdListRequest = {}): Promise<StashIdListResponse> {
    return this.apiCall<StashIdListResponse>("/posts/ids", {
      ...request,
      cursor: request.cursor ?? null,
    });
  }

  async getFilters(params: PostsFiltersRequest = {}): Promise<PostsFiltersResponse> {
    return this.apiCall<PostsFiltersResponse>("/posts/filters", params);
  }

  async getMapPosts(request: MapPostsRequest): Promise<MapPostsResponse> {
    return this.apiCall<MapPostsResponse>("/map/posts", {
      scope: "public",
      sort: "recent",
      limit: 30,
      ...request,
    });
  }

  async deletePost(postId: string, hard = false, requestGuard?: () => boolean): Promise<void> {
    if (!postId) {
      throw new Error("Post ID is required");
    }

    const path = hard ? `/posts/${postId}?hard=true` : `/posts/${postId}`;
    await apiClient.request(path, { method: "DELETE" }, false, false, requestGuard);
    // stash_deleted fires server-side via log_activity(DELETE_STASH).
  }

  async savePost(postId: string, origin?: "discover" | "public_search", requestGuard?: () => boolean): Promise<{ stasht_id: string; status: string; saved_at: string; is_saved: boolean }> {
    if (!postId) {
      throw new Error("Post ID is required");
    }

    const response = await apiClient.request<{
      stasht_id: string;
      status: string;
      saved_at: string;
      is_saved: boolean;
    }>(`/posts/${postId}`, { method: "POST", body: JSON.stringify(origin ? { origin } : {}) }, false, false, requestGuard);

    // stash_created fires server-side via log_activity(STASH_SAVED).
    return response;
  }

  // Link source URL to DM content
  async linkSource(
    postId: string,
    url: string,
  ): Promise<{
    status: "linked" | "merged";
    stasht_id: string;
    canonical_id?: string;
    original_id?: string;
    content_key: string;
    processing?: boolean;
    migration?: {
      migrated: boolean;
      tags_migrated: number;
      notes_migrated: boolean;
    };
  }> {
    if (!postId) {
      throw new Error("Post ID is required");
    }

    const response = await apiClient.post<{
      status: "linked" | "merged";
      stasht_id: string;
      canonical_id?: string;
      original_id?: string;
      content_key: string;
      processing?: boolean;
      migration?: {
        migrated: boolean;
        tags_migrated: number;
        notes_migrated: boolean;
      };
    }>(`/posts/${postId}/link-source`, { url });

    return response;
  }

  // Get one post through the same item path used by list hydration.
  async getPost(postId: string): Promise<Post | null> {
    return this.getPostById(postId);
  }

  // Get a single post through /posts/items.
  // skipAuth allows anonymous access (backend returns post without user-specific fields)
  // collectionId/inviteCode: when accessing a private post via a collection, pass these
  // so the backend can verify collection-context access instead of returning 403.
  async getPostById(
    postId: string,
    skipAuth = false,
    collectionId?: string,
    inviteCode?: string,
  ): Promise<Post | null> {
    if (!postId) {
      throw new Error("Post ID is required");
    }

    try {
      const response = await this.batchGetPosts(
        [postId],
        skipAuth,
        collectionId,
        inviteCode,
      );
      return response.posts[postId] ?? null;
    } catch (error) {
      if (error instanceof ApiClientError && error.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /** Explore: other people's public stashes matching a query, a page at a time (native public search). */
  async searchPublic(message: string, cursor?: string | null, limit = 20): Promise<PublicSearchPage> {
    return this.apiCall<PublicSearchPage>("/posts/search", { message, cursor: cursor ?? undefined, limit });
  }

  private async discoverRequest<T>(method: "POST" | "PATCH", body: unknown, session?: DiscoverSession, signal?: AbortSignal): Promise<T> {
    return apiClient.request<T>("/posts/discover", {
      method, body: JSON.stringify(body), signal,
    }, false, false, session?.isCurrent);
  }

  async discover(exclude: string[] = [], session?: DiscoverSession, signal?: AbortSignal): Promise<DiscoverPage> {
    return this.discoverRequest<DiscoverPage>("POST", { exclude }, session, signal);
  }

  async discoverFeedback(feedback:
    | { kind: "hide" | "unhide"; stasht_id: string }
    | { kind: "seen"; stasht_ids: string[] },
    session?: DiscoverSession,
  ): Promise<void> {
    await this.discoverRequest("PATCH", feedback, session);
  }

  // Batch get posts (great for normalizing search results!)
  // collectionId/inviteCode: optional collection context for accessing private posts
  async batchGetPosts(
    messageIds: string[],
    skipAuth = false,
    collectionId?: string,
    inviteCode?: string,
  ): Promise<PostsBatchResponse> {
    if (messageIds.length === 0) {
      return { posts: {}, not_found: [] };
    }

    const collectionFields: Record<string, string> = {};
    if (collectionId) collectionFields.collection = collectionId;
    if (inviteCode) collectionFields.invite = inviteCode;

    // API might have a limit, so chunk if needed
    const BATCH_SIZE = 50;
    if (messageIds.length > BATCH_SIZE) {
      const chunks: string[][] = [];
      for (let i = 0; i < messageIds.length; i += BATCH_SIZE) {
        chunks.push(messageIds.slice(i, i + BATCH_SIZE));
      }

      // Process chunks in parallel
      const results = await Promise.all(
        chunks.map(chunk =>
          this.apiCall<PostsBatchResponse>("/posts/items", {
            ids: chunk, // Backend expects 'ids', not 'message_mids'
            include_entities: true,
            ...collectionFields,
          }, skipAuth),
        ),
      );

      // Merge results
      const merged: PostsBatchResponse = {
        posts: {},
        not_found: [],
        user_states: {},
      };

      for (const result of results) {
        Object.assign(merged.posts, result.posts);
        merged.not_found.push(...result.not_found);
        merged.user_states = { ...merged.user_states, ...result.user_states };
      }

      return merged;
    }

    const body = {
      ids: messageIds, // Backend expects 'ids', not 'message_mids'
      include_entities: true,
      ...collectionFields,
    };

    const response = await this.apiCall<PostsBatchResponse>("/posts/items", body, skipAuth);

    return response;
  }

  // Get pipeline status for pending posts (reads from stasht_posts table)
  async getPipelineStatus(stasht_ids: string[]): Promise<PipelineStatusResponse> {
    if (stasht_ids.length === 0) {
      return { items: {}, requested: 0, found: 0 };
    }

    // Limit to 20 IDs per request
    const truncated = stasht_ids.slice(0, 20);

    return this.apiCall<PipelineStatusResponse>("/posts/pipeline-status", {
      ids: truncated,
    });
  }

  // Reprocess a post (re-run enrichment pipeline)
  async reprocessPost(postId: string): Promise<{ stasht_id: string; status: string; message: string }> {
    if (!postId) {
      throw new Error("Post ID is required");
    }

    const response = await apiClient.post<{ stasht_id: string; status: string; message: string }>(
      `/posts/${postId}/reprocess`,
      {},
    );

    return response;
  }

  async updateVisibility(
    postId: string,
    visibility: "public" | "private",
  ): Promise<{ stasht_id: string; visibility: string; updated_at: string }> {
    const response = await apiClient.patch<{
      stasht_id: string;
      visibility: string;
      updated_at: string;
    }>(`/posts/${postId}`, { visibility });
    return response;
  }

  async bulkUpdateVisibility(
    visibility: "public" | "private",
  ): Promise<{ updated_count: number; visibility: string }> {
    const response = await apiClient.post<{
      updated_count: number;
      visibility: string;
    }>("/posts/bulk-visibility", { visibility });
    return response;
  }

  async updateSensitive(
    postId: string,
    possiblySensitive: boolean,
  ): Promise<{ stasht_id: string; possibly_sensitive: boolean; updated_at: string }> {
    return apiClient.patch(`/posts/${postId}`, { possibly_sensitive: possiblySensitive });
  }

  // Update user-specific fields on a post (e.g. user_title)
  async updateUserTitle(
    postId: string,
    userTitle: string,
  ): Promise<{ stasht_id: string; user_title: string | null; updated_at: string }> {
    if (!postId) {
      throw new Error("Post ID is required");
    }

    const response = await apiClient.patch<{
      stasht_id: string;
      user_title: string | null;
      updated_at: string;
    }>(`/posts/${postId}`, { user_title: userTitle });

    return response;
  }
}

// Export singleton instance
export const postsService = PostsService.getInstance();

// Also export class for testing
export default PostsService;
